'use client'

// The birth onboarding — /test/onboarding (the proving-ground cut of the new
// signup experience; later this sequence replaces the real onboarding).
//
// One continuous scene, no navigation:
//   intro  — only the fade: whatever page "sign up" was pressed on goes to black
//            (there is no stand-in screen; the scene starts at once).
//   cell   — the brand cell, an orb of light at the exact center (the engine's
//            `cellGlow`), with small cells of light revealed round it one by
//            one — what a netti is made of — drifting up. hold it and the
//            organism is born under your finger: age scrubs up through the
//            imperative age bus, the viewport edges warm with the same hue, and
//            letting go lets it all ebb back.
//   born   — a full hold mints a random netti (futureIdentity of a fresh id),
//            the ground blooms from black into ONE solid colour of the minted
//            hue that keeps drifting, and the sentence becomes yours to
//            complete: "this is ___'s netti".
//   auth   — netti steps aside and the real account door (OAuth + magic link)
//            opens in place, laid out like the door itself (the form on the
//            left edge, google and github side by side, netti on the right),
//            and the water of the door rises round the newborn (StageWater):
//            its cells in the newborn's own colours, the carrying cells marked
//            "soon, in yours", what this netti will hold once it is kept.
//            nothing ever leaves the page. Once signed in, the dashboard seats
//            the newborn at its real size and the onboarding carries on there.
//
// The organism is the ONE canonical mount (MemoryOrganism, future engine),
// driven at 60fps through its age/drive buses — never a second renderer.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import MemoryOrganism from '../MemoryOrganism'
import OAuthButtons from '../OAuthButtons'
import EmailCodeEntry from '../EmailCodeEntry'
import BrandIcon from '../BrandIcon'
import StageWater, { type WaterPlace } from '../auth/StageWater'
import { Field, PillButton } from '../ui'
import { futureIdentity, futurePalette } from '../../lib/organism/future/identity'
import { chromeState, type ColorState } from '../../lib/organism/color'
import { MINTED_GROUND_LIGHT, MINTED_GROUND_SAT, mintedGround } from '../../lib/theme'
import { createClient } from '../../lib/supabase/client'
import { siteUrl } from '../../lib/site-url'
import { NETWORK_LINE, friendlyAuthError, friendlyThrown, isNetworkError } from '../../lib/auth-errors'
import { lockScroll } from '../../lib/os/scrollLock'
import type { RGB } from '../../lib/organism/color'
import './onboarding-birth.css'

type Stage = 'intro' | 'cell' | 'born' | 'auth'

// the black beneath everything — also handed to the engine so its internal
// blending agrees with the ground it floats on.
const BLACK: RGB = [0.031, 0.027, 0.039]

// the birth: ~3.3s of held intent; letting go ebbs back about twice as fast.
const HOLD_MS = 3300
const DECAY_MS = 1500
// the newborn's size — small but complete, never a fragment (the age law:
// natural sits near 0.62; a newborn reads whole from ~0.46).
const BORN_AGE = 0.58
// zero: the engine now keeps every petal folded until the organism has grown
// past a small floor, so a bloom scrubbed to zero is one luminous cell.
const SEED_AGE = 0
// the born ground — ONE solid colour of the minted hue, never a gradient. it
// keeps drifting around the wheel (slow) and breathing in lightness (slower),
// so the space above and below netti is always quietly becoming another colour.
// These are THE minted ground (lib/theme): the dashboard opens on exactly this
// wash at rest, so the room netti was born in is the room it lives in after.
const GROUND_SAT = MINTED_GROUND_SAT
const GROUND_LIGHT = MINTED_GROUND_LIGHT
// the same two numbers for the stylesheet's resting ground (.ob-root.is-born)
const GROUND_VARS = {
  '--ob-ground-sat': `${MINTED_GROUND_SAT * 100}%`,
  '--ob-ground-light': `${MINTED_GROUND_LIGHT * 100}%`,
} as React.CSSProperties
// ⚠️ the ground BREATHES around netti's hue; it never travels away from it.
// This used to drift 4.5°/s around the whole wheel, so half a minute after the
// birth a green netti was sitting on a peach ground — the one colour in the
// room that is supposed to be netti's own. It now sways ±14° and comes back.
const GROUND_SWAY_DEG = 14
const GROUND_SWAY_S = 11

// THE CELL AT REST is the biggest thing in the room and barely lit; as it is
// held it draws IN — making room for the petals — while the light around it
// spreads. Both are one gesture, driven by the same `eased`.
const REST_ORG_SCALE = 1.42
const REST_GLOW_SCALE = 0.52
const BORN_GLOW_SCALE = 2.7
const REST_GLOW_OPACITY = 0.22

function hslToRgb(h: number, s: number, l: number): RGB {
  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = l - c / 2
  const [r, g, b] =
    h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x]
  return [r + m, g + m, b + m]
}

/** the newborn's own colours for the water around it: seeds deep enough to
 *  read on its pale ground, orbs at the palette's mid light */
function waterPalette(p: ColorState): [number, number, number][] {
  const to255 = (c: RGB) => c.map((v) => Math.round(v * 255)) as [number, number, number]
  const pins = [p.primary, p.secondary, p.tinge ?? p.primary]
  return [
    to255(hslToRgb(pins[0].hue, 0.7, 0.42)),
    to255(hslToRgb(pins[1].hue, 0.72, 0.5)),
    to255(hslToRgb(pins[2].hue, 0.72, 0.54)),
  ]
}

const WIDE = '(min-width: 861px)'

const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v)
const smooth = (t: number) => t * t * (3 - 2 * t)
const lerpHue = (a: number, b: number, t: number) => {
  const d = ((b - a + 540) % 360) - 180
  return (((a + d * t) % 360) + 360) % 360
}

type Mint = {
  id: string
  seed: number
  /** the CURATED pins — NEVER the vivid ones. MemoryOrganism's adapter applies
   *  `vividColorState` on every setColor, so handing it already-vivid pins
   *  vivids them twice and the newborn renders hotter than the same creature
   *  seated under the dashboard. One pass, one creature (law_one_netti). */
  palette: ColorState
  tuners: Record<string, number>
  hue: number
}

/** a fresh netti — same law as a real user's (futureIdentity), from a random id. */
function mintNetti(): Mint {
  const id = `newborn-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`
  const identity = futureIdentity(id, 120)
  const palette = futurePalette(id)
  return {
    id,
    seed: identity.seed,
    palette,
    tuners: identity.tuners as Record<string, number>,
    // THE HUE THE EYE ACTUALLY READS. A palette's `primary` is one of three
    // pins, and the bloom fans them out — a netti minted on a violet primary
    // can render unmistakably red. `chromeState` recentres the palette on its
    // representative hue (the weighted circular mean), which is the same hue
    // the whole UI takes as "this organism's colour". The ground is netti's
    // home, so it has to be that hue and not a pin nobody can see.
    hue: chromeState(palette).primary.hue,
  }
}

// THE CELL AT REST — the organism itself, wearing almost no colour and almost
// no age: one big luminous cell and no petals yet. This is what you hold. There
// is no second cell drawn over it and nothing to cross-fade, so the thing that
// grows petals is literally the thing you were looking at.
// The engine ALWAYS draws its innermost whorl — the first petals' birth
// threshold is 0, so no age and no dial can take them to nothing (a newborn is
// never a fragment, by law). What we can do is keep them folded flat against a
// cell that dominates them: length at zero, width hairline, no scatter. The
// cell is the whole subject, with the faintest promise of what is folded in it.
// cellGlow: at rest the cell is an ORB of light, no limb, no drawn rim; the
// hold eases it back to the plain pearl the grown creature keeps.
const REST = { cellSize: 0.78, cellGlow: 1 }
const restState = (hue: number): ColorState => ({
  primary: { hue, sat: 0.05, light: 0.95 },
  secondary: { hue, sat: 0.04, light: 0.93 },
  tinge: { hue, sat: 0.06, light: 0.9 },
  contrast: 0.2,
})
/** what a dial settles to when a mint somehow lacks it (never in practice). */
const FALLBACK = { cellSize: 0.28, cellGlow: 0 }
const lerp = (a: number, b: number, t: number) => a + (b - a) * t
const lerpPin = (a: { hue: number; sat: number; light: number }, b: { hue: number; sat: number; light: number }, t: number) => ({
  hue: lerpHue(a.hue, b.hue, t),
  sat: lerp(a.sat, b.sat, t),
  light: lerp(a.light, b.light, t),
})
/** the palette on the way from the resting cell to the creature it becomes. */
function lerpState(a: ColorState, b: ColorState, t: number): ColorState {
  return {
    primary: lerpPin(a.primary, b.primary, t),
    secondary: lerpPin(a.secondary, b.secondary, t),
    ...(a.tinge && b.tinge ? { tinge: lerpPin(a.tinge, b.tinge, t) } : {}),
    contrast: lerp(a.contrast, b.contrast, t),
  }
}

// the birth speaks on its OWN channel: the page it opens over may carry a
// bloom of its own on the same window buses.
const BUS = 'birth'
const driveOrganism = (detail: Record<string, unknown>) => {
  window.dispatchEvent(new CustomEvent('innernet:organism-drive', { detail: { ...detail, bus: BUS } }))
}
const scrubAge = (age: number) => {
  window.dispatchEvent(new CustomEvent('innernet:organism-age', { detail: { age, bus: BUS } }))
}

export default function OnboardingBirth({
  overlay = false,
  autoStart = false,
  onLeave,
  initialName,
  onSkip,
  signedIn,
}: {
  /** fixed over whatever page opened it — the flush takes THAT page to black. */
  overlay?: boolean
  /** no stand-in intro: the flush begins the moment it mounts. */
  autoStart?: boolean
  /** the person stepped back out (the overlay unmounts). */
  onLeave?: () => void
  /** the name to offer in the naming field — for a person who already has an
   *  account, what the sign-in called them. */
  initialName?: string
  /** a quiet way past the birth, shown while the room is dark. The arrival
   *  (a person who already made their account at a side door) needs it; the
   *  front door does not offer one. */
  onSkip?: () => void
  /** THE ARRIVAL. The person already has an account, so there is no account
   *  door to open: keeping the netti hands it back here (the newborn note has
   *  already been left for the dashboard, exactly as the front door leaves it)
   *  and the caller decides where to go. */
  signedIn?: {
    onKeep: (born: { identity: string | null; owner: string | null }) => void
    /** the write is in flight: "keep it" shows its spinner and cannot be pressed twice. */
    keeping?: boolean
    /** the write did not land (offline, or a server that never answered): a kind line
     *  says so, and pressing "keep it" again is the retry. */
    failed?: boolean
    /** go on to the dashboard anyway — the newborn note this browser carries is
     *  adopted there, so nothing is lost. Offered only after a failure. */
    onContinue?: () => void
  }
} = {}) {
  const [stage, setStage] = useState<Stage>('intro')
  const [flushing, setFlushing] = useState(false)
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState(initialName ?? '')
  const [mint, setMint] = useState<Mint | null>(null)

  // auth moment (same door as /signup — OAuth + the email magic link)
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)
  const [sending, setSending] = useState(false)

  const rootRef = useRef<HTMLDivElement | null>(null)
  const edgeRef = useRef<HTMLDivElement | null>(null)
  const orgRef = useRef<HTMLDivElement | null>(null)
  const nameRef = useRef<HTMLInputElement | null>(null)
  const groundRef = useRef<HTMLDivElement | null>(null)
  /** the hue the ground is anchored to — netti's own, once it is born. */
  const groundBase = useRef(196)
  const glowRef = useRef<HTMLDivElement | null>(null)
  const fallbackRef = useRef<HTMLDivElement | null>(null)
  // ENGINE HEALTH — the birth must work on every machine. If WebGL2 is missing,
  // or the engine never says it is ready, a plain cell takes the stage and the
  // hold still mints a netti (the account adopts it and the dashboard, which
  // has its own fallbacks, shows the creature). Nothing here can strand a person.
  const [engine, setEngine] = useState<'booting' | 'ok' | 'failed'>('booting')
  const sizerRef = useRef<HTMLSpanElement | null>(null)
  const slotRef = useRef<HTMLDivElement | null>(null)
  const authColRef = useRef<HTMLDivElement | null>(null)
  // which room the account moment is laid out for (the door's own breakpoint)
  const [wide, setWide] = useState<boolean | null>(null)
  useEffect(() => {
    const mq = window.matchMedia(WIDE)
    const on = () => setWide(mq.matches)
    on()
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  // where the newborn stands, for the water around it: the slot's centre, and
  // the reach of the body inside it (the engine fits the body to ~0.3 of the
  // slot at the born focus); the form column's edge; on a phone, the form sits
  // under netti, so the cells quieten below it
  const locateNetti = useCallback((layer: HTMLElement): WaterPlace | null => {
    const slot = slotRef.current?.getBoundingClientRect()
    if (!slot || !slot.width) return null
    const lr = layer.getBoundingClientRect()
    const r = slot.width * 0.3
    const heart = { x: slot.left + slot.width / 2 - lr.left, y: slot.top + slot.height / 2 - lr.top, r }
    const col = authColRef.current?.getBoundingClientRect()
    return {
      heart,
      formRight: wide && col ? col.right + 32 - lr.left : 0,
      quietBelow: wide ? 0 : heart.y + r * 1.15,
    }
  }, [wide])
  // the resting cell, for the cells revealed round it: its body is ~a fifth of
  // the slot, and nothing needs to stay quiet
  const locateCell = useCallback((layer: HTMLElement): WaterPlace | null => {
    const slot = slotRef.current?.getBoundingClientRect()
    if (!slot || !slot.width) return null
    const lr = layer.getBoundingClientRect()
    return {
      heart: { x: slot.left + slot.width / 2 - lr.left, y: slot.top + slot.height / 2 - lr.top, r: slot.width * 0.2 },
      formRight: 0,
    }
  }, [])
  const groundHue = useRef(196)

  const stageRef = useRef<Stage>('intro')
  stageRef.current = stage
  const holding = useRef(false)
  const progress = useRef(0)
  const cycleHue = useRef(196)
  const raf = useRef(0)
  const mintRef = useRef(mint)
  mintRef.current = mint

  // mint the first candidate on the client only (Math.random never runs in SSR)
  useEffect(() => {
    setMint(mintNetti())
  }, [])
  // ESCAPE LEAVES, AT EVERY STAGE before the account exists: the flush, the cell,
  // the newborn, the account door. It used to work only at the cell, so a person who
  // had held the cell and wasn't ready to give an email had no way back to the page
  // beneath except a reload. The overlay unmounts (its scroll lock and the page's
  // bloom release in their own cleanups); the hold is a pointer or Space/Enter
  // gesture and never reads Escape, so this cannot interrupt a birth in progress.
  // The visible twin is the quiet "not now" below.
  useEffect(() => {
    if (!overlay || !onLeave) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onLeave() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [overlay, onLeave])

  // Hand the engine the mint's BODY (petal geometry, spread, formation) at once
  // — it is invisible until the petals are born — but hold the colour and the
  // cell at REST. What shows now is one luminous cell; the hold turns it into
  // this creature without anything ever being swapped out.
  const applyMint = useCallback((m: Mint) => {
    driveOrganism({
      color: restState(cycleHue.current),
      tuners: { ...m.tuners, ...REST },
    })
  }, [])
  useEffect(() => {
    if (!mint) return
    const onReady = () => { setEngine('ok'); applyMint(mint) }
    window.addEventListener('innernet:organism-ready', onReady)
    return () => window.removeEventListener('innernet:organism-ready', onReady)
  }, [mint, applyMint])
  useEffect(() => {
    let gl: WebGL2RenderingContext | null = null
    try { gl = document.createElement('canvas').getContext('webgl2') } catch { gl = null }
    if (!gl) { setEngine('failed'); return }
    // the engine boots asynchronously; give it a real chance, then stand in.
    const t = window.setTimeout(() => setEngine((e) => (e === 'booting' ? 'failed' : e)), 4500)
    return () => window.clearTimeout(t)
  }, [])

  const complete = useCallback(() => {
    const m = mintRef.current
    holding.current = false
    progress.current = 1
    scrubAge(BORN_AGE)
    // the arrival: one firing of the nervous system + the ground blooms into
    // the house wash of the minted hue (painted locally, never onto :root).
    const hue = m ? m.hue : cycleHue.current
    groundHue.current = hue
    groundBase.current = hue
    driveOrganism({
      fire: 1.3,
      bg: mintedGround(hue),
      focus: { angle: 0, dist: 0, zoom: 1.28 },
      ...(m ? { color: m.palette, tuners: m.tuners } : {}),
    })
    rootRef.current?.style.setProperty('--ob-hue', String(Math.round(hue)))
    if (groundRef.current) groundRef.current.style.backgroundColor = `hsl(${hue.toFixed(0)} ${GROUND_SAT * 100}% ${GROUND_LIGHT * 100}%)`
    if (edgeRef.current) edgeRef.current.style.opacity = '0'
    // the light around the cell has done its work — it goes out as the room
    // takes netti's colour. (Written inline, because inline beats the class.)
    if (glowRef.current) glowRef.current.style.opacity = '0'
    if (orgRef.current) orgRef.current.style.transform = 'scale(1)'
    setStage('born')
  }, [])

  // the living loop — hue drift + the held birth. everything it writes is an
  // inline style or a window event; React never re-renders at 60fps.
  useEffect(() => {
    if (stage !== 'cell') return
    let last = performance.now()
    const tick = (now: number) => {
      // the hold is measured in REAL time. A clamped frame delta dilates it on
      // a machine whose frames stall (the birth then takes 6s instead of 3),
      // and a machine whose rAF throttles could never finish it at all. The hue
      // drift keeps a clamp so a hidden tab never jumps it.
      const real = Math.min(250, now - last)
      const dt = Math.min(64, real)
      last = now
      const root = rootRef.current
      const m = mintRef.current

      cycleHue.current = (cycleHue.current + dt * 0.014) % 360
      const p = (progress.current = clamp01(
        progress.current + (holding.current ? real / HOLD_MS : -real / DECAY_MS),
      ))
      const eased = smooth(p)

      // the glow's hue wanders while the cell waits, then commits to the minted
      // hue as the birth takes — organism, edge and text all breathe in sync.
      const hue = m ? lerpHue(cycleHue.current, m.hue, eased) : cycleHue.current
      root?.style.setProperty('--ob-hue', hue.toFixed(1))

      // birth: the age bus scrubs the organism from seed to newborn, spinning
      // slightly as its petals are born; the cell dissolves as the body forms.
      // ONE CELL, GROWING. The age opens the petals out of the very cell that
      // was sitting there; the cell itself eases from its resting size down to
      // the size this creature keeps; the colour travels from near-white to the
      // palette it was minted with. Nothing fades in over anything.
      scrubAge(SEED_AGE + eased * (BORN_AGE - SEED_AGE))
      driveOrganism({
        rotation: eased * 3.1,
        focus: { angle: 0, dist: 0, zoom: 1 + eased * 0.28 },
        // the petals GROW OUT of the cell that was already there — every dial
        // here is a continuous shape the engine travels through, never a count.
        tuners: Object.fromEntries(
          (Object.keys(REST) as (keyof typeof REST)[]).map((k) => [
            k,
            lerp(REST[k], m?.tuners[k] ?? FALLBACK[k], eased),
          ]),
        ),
        ...(m ? { color: lerpState(restState(hue), m.palette, eased) } : {}),
      })

      // ONE GESTURE: the cell draws IN (the whole body eases down from its
      // resting size) while the light around it SPREADS and brightens.
      if (orgRef.current) orgRef.current.style.transform = `scale(${lerp(REST_ORG_SCALE, 1, eased).toFixed(3)})`
      if (glowRef.current) {
        glowRef.current.style.transform = `translate(-50%, -50%) scale(${lerp(REST_GLOW_SCALE, BORN_GLOW_SCALE, eased).toFixed(3)})`
        glowRef.current.style.opacity = lerp(REST_GLOW_OPACITY, 1, eased).toFixed(3)
      }
      if (fallbackRef.current) {
        fallbackRef.current.style.transform = `scale(${(1 + eased * 0.9).toFixed(3)})`
        fallbackRef.current.style.setProperty('--ob-mix', (eased * 100).toFixed(1) + '%')
      }

      // the viewport edges warm with the same light until it reaches the center
      const edge = edgeRef.current
      if (edge) {
        edge.style.opacity = String(clamp01(p * 1.5))
        edge.style.boxShadow = `inset 0 0 ${(eased * 56).toFixed(1)}vmax ${(eased * 12).toFixed(1)}vmin hsl(${hue.toFixed(0)} 82% 66% / 0.7)`
      }

      if (p >= 1) {
        complete()
        return
      }
      raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf.current)
  }, [stage, complete])

  // the born ground keeps living — but it lives AROUND netti's colour: a slow
  // sway either side of the minted hue, and a breath in light and saturation.
  // Solid colour only, never a gradient.
  useEffect(() => {
    if (stage !== 'born' && stage !== 'auth') return
    let last = performance.now()
    let frames = 0
    let id = 0
    const tick = (now: number) => {
      const dt = Math.min(64, now - last)
      last = now
      void dt
      const sway = Math.sin((now / 1000 / GROUND_SWAY_S) * Math.PI * 2) * GROUND_SWAY_DEG
      const h = (groundHue.current = (((groundBase.current + sway) % 360) + 360) % 360)
      const breathe = Math.sin(now / 1000 / 2.6)
      const l = GROUND_LIGHT + breathe * 0.02 + (stage === 'auth' ? 0.012 : 0)
      const sat = GROUND_SAT + breathe * 0.05
      const ground = `hsl(${h.toFixed(1)} ${(sat * 100).toFixed(1)}% ${(l * 100).toFixed(1)}%)`
      if (groundRef.current) groundRef.current.style.backgroundColor = ground
      rootRef.current?.style.setProperty('--ob-hue', h.toFixed(1))
      // the ground itself, for anything that must sit IN it (the carrying
      // cells' light is mixed from this, so it is always the room's colour)
      rootRef.current?.style.setProperty('--ob-ground', ground)
      if (++frames % 45 === 0) driveOrganism({ bg: hslToRgb(h, sat, l) })
      id = requestAnimationFrame(tick)
    }
    id = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(id)
  }, [stage])

  // the name underline is exactly as long as the name: the input is sized to
  // a hidden twin of its text (placeholder included), never a guessed width.
  useEffect(() => {
    if (!naming) return
    const input = nameRef.current
    const sizer = sizerRef.current
    if (!input || !sizer) return
    input.style.width = `${Math.max(sizer.offsetWidth, 18)}px`
  }, [naming, name])

  // ---- interactions ---------------------------------------------------------

  const begin = () => {
    setFlushing(true)
    window.setTimeout(() => {
      setStage('cell')
      setFlushing(false)
    }, 1100)
  }
  // the scene starts at once: the page fades to black and the cell arrives.
  // (There is no stand-in intro any more: whoever is here pressed "sign up"
  // somewhere already. `autoStart` stays for the callers that pass it.)
  useEffect(() => {
    const t = window.setTimeout(() => begin(), 60)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart])
  // as an overlay the page beneath must not scroll under the birth
  useEffect(() => (overlay ? lockScroll('birth') : undefined), [overlay])
  // …and once the flush has taken the page, the page's own bloom stands still
  // so the birth has the GPU to itself. Released the moment the overlay leaves.
  useEffect(() => {
    if (!overlay) return
    const hold = (on: boolean) =>
      window.dispatchEvent(new CustomEvent('innernet:organism-hold', { detail: { hold: on, except: BUS } }))
    if (stage !== 'intro') hold(true)
    return () => { hold(false) }
  }, [overlay, stage])

  const holdStart = (e?: React.PointerEvent) => {
    if (stageRef.current !== 'cell') return
    if (e) e.currentTarget.setPointerCapture?.(e.pointerId)
    holding.current = true
  }
  const holdEnd = () => {
    holding.current = false
  }
  useEffect(() => {
    // a hold must never stick: leaving the tab or window releases it
    window.addEventListener('blur', holdEnd)
    return () => window.removeEventListener('blur', holdEnd)
  }, [])

  const tryAgain = () => {
    const next = mintNetti()
    setMint(next)
    setNaming(false)
    setName('')
    progress.current = 0
    scrubAge(SEED_AGE)
    applyMint(next)
    driveOrganism({ bg: BLACK, rotation: 0, focus: null })
    groundHue.current = cycleHue.current
    groundBase.current = cycleHue.current
    if (orgRef.current) orgRef.current.style.transform = `scale(${REST_ORG_SCALE})`
    if (glowRef.current) {
      glowRef.current.style.transform = `translate(-50%, -50%) scale(${REST_GLOW_SCALE})`
      glowRef.current.style.opacity = String(REST_GLOW_OPACITY)
    }
    setStage('cell')
  }

  const openAuth = () => {
    // remember the newborn so the signed-in dashboard can adopt it (later part)
    const m = mintRef.current
    // the ground rides along: on a host that has never seen this person the
    // boot script paints it from here, so the dashboard's first frame is this
    // room's resting colour (the account saves the same value on adoption).
    const ground = m ? mintedGround(m.hue).map((c) => Math.round(c * 1000) / 1000) : null
    const payload = JSON.stringify({ identity: m?.id ?? null, owner: name.trim() || null, at: Date.now(), ...(ground ? { ground } : {}) })
    try { localStorage.setItem('innernet:onboarding-netti', payload) } catch { /* private mode */ }
    try {
      // ⚠️ the dashboard lives on ANOTHER origin (yours.innernet.live) and
      // localStorage is per-origin — the note above is invisible there. The
      // session cookie already spans the apex + subdomain (COOKIE_DOMAIN), so
      // the newborn rides the same way. 7 days; the dashboard clears it.
      const domain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN
      const secure = location.protocol === 'https:' ? '; Secure' : ''
      document.cookie = `innernet_newborn=${encodeURIComponent(payload)}; path=/; max-age=604800; SameSite=Lax${domain ? `; domain=${domain}` : ''}${secure}`
    } catch { /* cookies off — localStorage stands */ }
    // an account already stands behind this person — there is no door to open.
    if (signedIn) { signedIn.onKeep({ identity: m?.id ?? null, owner: name.trim() || null }); return }
    setStage('auth')
  }

  // the buttons' own sentences pass through; the browser's "Failed to fetch" does not
  const oauthFailed = (msg: string) => setError(isNetworkError(msg) ? NETWORK_LINE : msg)

  const sendMagicLink = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) {
      setError('enter your email first.')
      return
    }
    setSending(true)
    setError('')
    setSent(false)
    try {
      const supabase = createClient()
      const { error: err } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: `${siteUrl()}/auth/land?next=${encodeURIComponent('/dashboard')}` },
      })
      if (err) {
        setError(friendlyAuthError(err.message))
        setSending(false)
        return
      }
      setSent(true)
    } catch (err) {
      // never String(err): a rejected fetch reads "TypeError: Failed to fetch"
      setError(friendlyThrown(err))
    }
    setSending(false)
  }

  useEffect(() => {
    if (naming) nameRef.current?.focus()
  }, [naming])

  // stable so the colour prop pushes exactly once, at birth
  const rest = useMemo(() => restState(196), [])
  const trimmed = name.trim()
  // THE WAY OUT, VISIBLE. Only where there is somewhere to go back to: the overlay
  // that opened over a page (the landing's "create yours"). Escape does the same.
  const notNow =
    overlay && onLeave ? (
      <button type="button" className="quiet-link" onClick={onLeave}>
        not now
      </button>
    ) : null
  const rootCls = [
    'ob-root',
    overlay ? 'is-overlay' : '',
    autoStart ? 'is-auto' : '',
    stage === 'cell' ? 'is-cell' : '',
    flushing ? 'is-flushing' : '',
    stage === 'born' ? 'is-born' : '',
    stage === 'auth' ? 'is-auth' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div ref={rootRef} className={rootCls} style={GROUND_VARS}>
      {/* the born ground — black until the mint, then the house wash */}
      <div ref={groundRef} className="ob-ground" aria-hidden />
      <div ref={edgeRef} className="ob-edge" aria-hidden />
      <div className="ob-burst" aria-hidden />

      {/* ---- the shared center stage (cell → birth → born) ---- */}
      <div className="ob-stage" aria-live="polite">
        <div className="ob-copy">
          {stage === 'cell' ? (
            <>
              <h1 className="ob-head ob-in" style={{ ['--i' as string]: '1' }}>
                this is a cell.
              </h1>
              <p className="ob-sub ob-in" style={{ ['--i' as string]: '2' }}>
                one cell holds one piece of your memory. a decision, a fact about you, a
                conversation worth keeping. your netti grows out of it.
              </p>
            </>
          ) : stage === 'born' || stage === 'auth' ? (
            <h1 className="ob-head ob-in" style={{ ['--i' as string]: '0' }}>
              {naming ? (
                <>
                  this is{' '}
                  <input
                    ref={nameRef}
                    className="ob-name"
                    value={name}
                    onChange={(e) => setName(e.target.value.slice(0, 24))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && trimmed) openAuth()
                    }}
                    placeholder="your name"
                    aria-label="your name"
                    autoComplete="off"
                    autoCapitalize="off"
                    spellCheck={false}
                  />
                  <span ref={sizerRef} className="ob-name-sizer" aria-hidden>
                    {name || 'your name'}
                  </span>
                  {'’s netti'}
                </>
              ) : (
                'this is your netti'
              )}
            </h1>
          ) : null}
        </div>

        <div ref={slotRef} className="ob-slot">
          {/* the glow the cell sits in — light around it, never a second cell.
              the outer ring is scaled by the hold; the inner one breathes. */}
          <div ref={glowRef} className="ob-glow" aria-hidden><i /></div>

          {engine === 'failed' ? (
            // the stand-in: a plain cell that grows and takes the mint's colour
            // as it is held — on a machine with no engine, the birth still works.
            <div ref={fallbackRef} className="ob-cell-fallback" aria-hidden>
              <div className="ob-cell-fallback-i" />
            </div>
          ) : null}

          {mint && engine !== 'failed' ? (
            <div ref={orgRef} className="ob-organism" aria-hidden>
              <MemoryOrganism
                live={false}
                spin
                ageBus
                driveBus
                busKey={BUS}
                publishHue={false}
                age={SEED_AGE}
                seed={mint.seed}
                // the resting cell until it is born, then the palette it keeps.
                // ONE prop change in the whole sequence, at the moment of birth.
                color={stage === 'cell' || stage === 'intro' ? rest : mint.palette}
                bg={BLACK}
                mode="focus"
                renderScale={1}
                maxDpr={1.75}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
              />
            </div>
          ) : null}

          {stage === 'cell' ? (
            // the hit target only — the cell you are holding is the organism's
            // own, drawn by the engine from the brand's polar law. There is
            // nothing here to see and so nothing to cross-fade.
            <div
              className="ob-hit"
              role="button"
              tabIndex={0}
              aria-label="hold to create your netti"
              onPointerDown={holdStart}
              onPointerUp={holdEnd}
              onPointerCancel={holdEnd}
              onContextMenu={(e) => e.preventDefault()}
              onKeyDown={(e) => {
                if (e.key === ' ' || e.key === 'Enter') {
                  e.preventDefault()
                  holdStart()
                }
              }}
              onKeyUp={holdEnd}
            />
          ) : null}
        </div>

        <div className="ob-below">
          {stage === 'cell' ? (
            <p className="ob-hold ob-in" style={{ ['--i' as string]: '3' }}>
              hold the cell to create your <b>netti</b>
            </p>
          ) : stage === 'born' || stage === 'auth' ? (
            <>
              <p className="ob-sub ob-in" style={{ ['--i' as string]: '1' }}>
                it is made of what you keep. every ai you connect adds to the same memory, and
                your netti grows with it.
              </p>
              <div className="ob-ctas ob-in" style={{ ['--i' as string]: '2' }}>
                {naming ? (
                  <PillButton
                    icon={<BrandIcon name="innernet" size={22} className="pill-netti" />}
                    onClick={openAuth}
                    loading={signedIn?.keeping}
                  >
                    {signedIn?.keeping ? 'keeping it…' : signedIn ? 'keep it' : 'sign up to save your netti'}
                  </PillButton>
                ) : (
                  <PillButton
                    icon={<BrandIcon name="innernet" size={22} className="pill-netti" />}
                    onClick={() => setNaming(true)}
                  >
                    put your name on it
                  </PillButton>
                )}
                {signedIn?.failed && !signedIn.keeping ? (
                  <p className="ob-sub" role="alert" style={{ margin: 0 }}>
                    we couldn&apos;t keep your netti just now. please check your connection and press keep it again.
                  </p>
                ) : null}
                {/* the quiet ways on, side by side so a failure line never pushes them off a short screen */}
                <div style={{ display: 'flex', gap: '0.4rem 1.3rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <button type="button" className="quiet-link" onClick={tryAgain} disabled={signedIn?.keeping}>
                    create another
                  </button>
                  {signedIn?.failed && !signedIn.keeping && signedIn.onContinue ? (
                    <button type="button" className="quiet-link" onClick={signedIn.onContinue}>
                      continue anyway
                    </button>
                  ) : null}
                  {notNow}
                </div>
              </div>
            </>
          ) : null}
        </div>

        {/* the quiet login door, only while the room is dark. A CHILD of the
            stage: the stage is the box that is exactly one viewport tall, so
            anything outside it is outside the screen. */}
        {stage === 'cell' ? (
          <div className="ob-login ob-in" style={{ ['--i' as string]: '4' }}>
            {onSkip ? (
              // the arrival's one quiet exit: their netti is minted for them
              // under the name we offered, and they go on to where they were.
              <button type="button" className="quiet-link" onClick={onSkip}>
                skip this for now
              </button>
            ) : (
              <a className="quiet-link" href="/login">
                already have a netti? log in
              </a>
            )}
            {notNow}
          </div>
        ) : null}
      </div>

      {/* ---- the cells, revealed one by one round the resting cell ---- */}
      {stage === 'cell' && wide !== null ? (
        <StageWater
          className="ob-water ob-water--cell"
          room={wide ? 'wide' : 'narrow'}
          locate={locateCell}
          dir={-1}
          count={wide ? 46 : 28}
          cards={false}
          stagger={0.16}
        />
      ) : null}

      {/* ---- the water of the door, round the newborn ---- */}
      {stage === 'auth' && wide !== null ? (
        <StageWater
          key={wide ? 'wide' : 'narrow'}
          className="ob-water"
          room={wide ? 'wide' : 'narrow'}
          locate={locateNetti}
          dir={-1}
          count={wide ? 110 : 40}
          cards={wide}
          ink
          palette={mint ? waterPalette(mint.palette) : undefined}
          foretell
        />
      ) : null}

      {/* ---- the account door — the same signup language, in place ---- */}
      {stage === 'auth' ? (
        <div className="ob-auth">
          <div ref={authColRef} className="ob-auth-col">
            <h2 className="ob-head ob-in" style={{ ['--i' as string]: '0' }}>
              keep {trimmed ? `${trimmed}'s netti` : 'your netti'}.
            </h2>
            <p className="ob-sub ob-in" style={{ ['--i' as string]: '1' }}>
              your account keeps it, along with the memory it grows from. every ai you use reads
              that same memory.
            </p>
            <div className="ob-in" style={{ ['--i' as string]: '2' }}>
              <OAuthButtons next="/dashboard" verb="begin with" layout="row" onError={oauthFailed} />
            </div>
            {error ? (
              <p className="ob-sub" style={{ color: 'var(--danger, #c4502e)' }}>
                {error}
              </p>
            ) : null}
            {sent ? (
              <div className="ob-in" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
                <p className="ob-sub" style={{ margin: 0 }}>
                  check your email. one tap on the link signs you in, or type the code
                  here.
                </p>
                <EmailCodeEntry email={email} next="/dashboard" onChangeAddress={() => { setSent(false); setError('') }} />
              </div>
            ) : (
              <form onSubmit={sendMagicLink} className="begin-magic ob-in" style={{ ['--i' as string]: '3' }}>
                <div className="begin-or">
                  <span>or with your email</span>
                </div>
                <Field
                  label="your email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <PillButton
                  type="submit"
                  className="cta-pill"
                  loading={sending}
                  icon={<BrandIcon name="innernet" size={22} className="pill-netti" />}
                >
                  {sending ? 'sending…' : 'begin'}
                </PillButton>
              </form>
            )}
            <div className="ob-auth-links ob-in" style={{ ['--i' as string]: '4' }}>
              <a className="quiet-link" href="/login">
                already have an account? log in
              </a>
              <button type="button" className="quiet-link" onClick={() => setStage('born')}>
                back
              </button>
              {notNow}
            </div>
          </div>
          <div aria-hidden />
        </div>
      ) : null}

      {/* ---- the intro — a stand-in for any "sign up" tap ---- */}
      {stage === 'intro' ? (
        <>
          <div className="ob-flush" aria-hidden />
        </>
      ) : null}
    </div>
  )
}
